import React, { useEffect, useMemo, useState } from 'react';
import API from 'shared/api';
import { useAuth } from 'shared/context/AuthContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import {
  FiBarChart2,
  FiPackage,
  FiLayers,
  FiLock,
  FiUsers,
  FiUserCheck,
  FiCpu,
  FiShoppingBag,
} from 'react-icons/fi';
import { S, rangoDePreset } from './Reportes/reportesCommon';
import FiltrosGlobales from './Reportes/FiltrosGlobales';
import ReporteVentas from './Reportes/ReporteVentas';
import ReporteInventario from './Reportes/ReporteInventario';
import ReporteProduccion from './Reportes/ReporteProduccion';
import ReporteCompras from './Reportes/ReporteCompras';
import ReporteCierres from './Reportes/ReporteCierres';
import ReporteClientes from './Reportes/ReporteClientes';
import ReporteUsuarios from './Reportes/ReporteUsuarios';
import ReporteIA from './Reportes/ReporteIA';

const CATEGORIAS = [
  { id: 'ventas', label: 'Ventas', icon: FiBarChart2, permiso: 'reportes.ventas', desc: 'Total, detallado y por producto', depto: true },
  { id: 'inventario', label: 'Inventario', icon: FiPackage, permiso: 'reportes.inventario', desc: 'Movimientos, valorización y rotación' },
  { id: 'compras', label: 'Compras', icon: FiShoppingBag, permiso: 'reportes.compras', desc: 'Resumen, proveedores y cuentas por pagar' },
  { id: 'produccion', label: 'Producción', icon: FiLayers, permiso: 'reportes.produccion', desc: 'Órdenes, costos y mermas', tipoRestaurante: true },
  { id: 'cierres', label: 'Cierres de Caja', icon: FiLock, permiso: 'reportes.cierres', desc: 'Resumen, detalle y diferencias', tipoRestaurante: true },
  { id: 'clientes', label: 'Clientes', icon: FiUsers, permiso: 'reportes.clientes', desc: 'Resumen y top clientes' },
  { id: 'usuarios', label: 'Usuarios', icon: FiUserCheck, permiso: 'reportes.usuarios', desc: 'Rendimiento y asistencia' },
  { id: 'ia', label: 'IA', icon: FiCpu, permiso: 'reportes.ia', desc: 'Calidad, costo y latencia' },
];

export default function Reportes() {
  const { tienePermiso, isTipoEmpresa } = useAuth();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const [sedes, setSedes] = useState([]);
  const [deptos, setDeptos] = useState([]);
  const [filtros, setFiltros] = useState(() => {
    const [a, b] = rangoDePreset('30d');
    return { preset: '30d', fechaDesde: a, fechaHasta: b, sede: '', departamento: '' };
  });

  useEffect(() => {
    API.get('/usuarios/sedes')
      .then((r) => {
        if (r.data.status === 'success') setSedes(r.data.data || []);
      })
      .catch(() => {});
    API.get('/inventario/departamentos')
      .then((r) => {
        if (r.data.status === 'success') setDeptos(r.data.data || []);
      })
      .catch(() => {});
  }, []);

  // Categorias visibles segun permiso y tipo de empresa.
  const categorias = useMemo(
    () =>
      CATEGORIAS.filter((c) => {
        if (!tienePermiso(c.permiso)) return false;
        if (c.tipoRestaurante && isTipoEmpresa('importador', 'general')) return false;
        return true;
      }),
    [tienePermiso, isTipoEmpresa]
  );

  const [activa, setActiva] = useState('');
  useEffect(() => {
    if (!activa && categorias.length > 0) setActiva(categorias[0].id);
    if (activa && !categorias.some((c) => c.id === activa)) setActiva(categorias[0]?.id || '');
  }, [categorias, activa]);

  const renderPanel = () => {
    const showDepto = categorias.find((c) => c.id === activa)?.depto === true;
    switch (activa) {
      case 'ventas':
        return (
          <>
            <FiltrosGlobales filtros={filtros} setFiltros={setFiltros} sedes={sedes} deptos={deptos} mostrarDepartamento={showDepto} />
            <ReporteVentas filtros={filtros} />
          </>
        );
      case 'inventario':
        return (
          <>
            <FiltrosGlobales filtros={filtros} setFiltros={setFiltros} sedes={sedes} deptos={deptos} />
            <ReporteInventario filtros={filtros} />
          </>
        );
      case 'compras':
        return (
          <>
            <FiltrosGlobales filtros={filtros} setFiltros={setFiltros} sedes={sedes} deptos={deptos} />
            <ReporteCompras filtros={filtros} />
          </>
        );
      case 'produccion':
        return (
          <>
            <FiltrosGlobales filtros={filtros} setFiltros={setFiltros} sedes={sedes} deptos={deptos} />
            <ReporteProduccion filtros={filtros} />
          </>
        );
      case 'cierres':
        return (
          <>
            <FiltrosGlobales filtros={filtros} setFiltros={setFiltros} sedes={sedes} deptos={deptos} />
            <ReporteCierres filtros={filtros} />
          </>
        );
      case 'clientes':
        return (
          <>
            <FiltrosGlobales filtros={filtros} setFiltros={setFiltros} sedes={sedes} deptos={deptos} />
            <ReporteClientes filtros={filtros} />
          </>
        );
      case 'usuarios':
        return (
          <>
            <FiltrosGlobales filtros={filtros} setFiltros={setFiltros} sedes={sedes} deptos={deptos} />
            <ReporteUsuarios filtros={filtros} />
          </>
        );
      case 'ia':
        return (
          <>
            <FiltrosGlobales filtros={filtros} setFiltros={setFiltros} sedes={sedes} deptos={deptos} />
            <ReporteIA filtros={filtros} />
          </>
        );
      default:
        return null;
    }
  };

  const railBtn = (c) => {
    const active = c.id === activa;
    return (
      <button
        key={c.id}
        onClick={() => setActiva(c.id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 12px',
          borderRadius: '10px',
          border: `1px solid ${active ? 'transparent' : 'transparent'}`,
          background: active ? 'var(--agx-blue-bg)' : 'transparent',
          color: active ? '#2563eb' : 'inherit',
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          width: isMobile ? 'auto' : '100%',
          boxSizing: 'border-box',
        }}
      >
        <c.icon size={18} style={{ flexShrink: 0 }} />
        {!isMobile && (
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '13px', fontWeight: 700 }}>{c.label}</span>
            <span style={{ display: 'block', fontSize: '10px', color: 'var(--agx-muted)', whiteSpace: 'normal' }}>
              {c.desc}
            </span>
          </span>
        )}
        {isMobile && <span style={{ fontSize: '12px', fontWeight: 700 }}>{c.label}</span>}
      </button>
    );
  };

  if (categorias.length === 0) {
    return (
      <div style={S.page}>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--agx-muted)' }}>
          No tienes permisos para ver reportes.
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>
            <FiBarChart2 size={20} style={{ verticalAlign: '-3px', marginRight: '8px' }} />
            Reportes
          </div>
          <div style={S.subtitle}>Informes organizados por área del sistema.</div>
        </div>
      </div>

      <div style={{ ...S.layout, flexDirection: isMobile ? 'column' : 'row' }}>
        <nav style={isMobile ? S.railMobile : S.rail} aria-label="Categorías de reportes">
          {categorias.map(railBtn)}
        </nav>
        <div style={S.content}>
          <div style={{ marginBottom: '14px' }}>{renderPanel()}</div>
        </div>
      </div>
    </div>
  );
}
